import React, { createContext, useContext, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useConstant } from '@pushpay/utils';
import { getNextId } from './get-id';
const ExpandGroupContext = /*#__PURE__*/createContext(null);
export const ExpandGroupProvider = ({
  expandMultiple,
  children
}) => {
  const expandedItems = useConstant(() => new Set());
  const [expandStatusGetter, setExpandStatusGetter] = useState(() => uuid => expandedItems.has(uuid) ? 'expanded' : 'collapsed');
  const expandStatusToggled = useRef(false);
  const register = useCallback((uuid, expandStatus) => {
    if (expandStatus !== 'collapsed') {
      expandedItems.add(uuid);
    }

    return () => expandedItems.delete(uuid);
  }, [expandedItems]);
  const toggleExpandStatus = useCallback(uuid => {
    if (expandedItems.has(uuid)) {
      expandedItems.delete(uuid);
    } else {
      if (!expandMultiple) {
        expandedItems.clear();
      }

      expandedItems.add(uuid);
    }

    if (!expandStatusToggled.current) {
      expandStatusToggled.current = true;
    }

    setExpandStatusGetter(() => target => expandedItems.has(target) ? 'expanded' : 'collapsed');
  }, [expandedItems]);
  const value = useMemo(() => ({
    getExpandStatus: expandStatusGetter,
    toggleExpandStatus,
    register,
    expandStatusToggled
  }), [expandStatusGetter, toggleExpandStatus, register, expandStatusToggled]);
  return /*#__PURE__*/React.createElement(ExpandGroupContext.Provider, {
    value: value
  }, children);
};
export function useExpandGroup(initialExpandStatus) {
  const context = useContext(ExpandGroupContext);

  if (!context) {
    throw new Error('useExpandGroup must be used within an ExpandGroup');
  }

  const {
    getExpandStatus,
    toggleExpandStatus,
    register,
    expandStatusToggled
  } = context;
  const uuid = useConstant(getNextId);
  const unregister = useConstant(() => register(uuid, initialExpandStatus));
  const preexpanded = useRef(initialExpandStatus === 'preexpanded');
  const updateExpanded = useCallback(() => toggleExpandStatus(uuid), [uuid, toggleExpandStatus]);
  useEffect(() => unregister, []);
  const expandStatus = getExpandStatus(uuid);
  return {
    expandStatus: preexpanded.current && !expandStatusToggled.current ? 'preexpanded' : expandStatus,
    updateExpanded
  };
}